import React, { useMemo } from 'react';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { Badge } from 'embeds/components';
import Avatar, { AvatarProps } from 'embeds/components/Avatar';
import { get } from 'lodash';
import { axiosService } from 'ui-model';
import { getInitials, stringToColor } from 'ui-utils';

import configService from '@lib/services/configService';

import { AvatarNode_EmbedNode$key } from './__generated__/AvatarNode_EmbedNode.graphql';

const styles = ({
  palette: {
    common: { white },
  },
  custom: {
    palette: { colorAccentAloe },
  },
}: Theme) =>
  createStyles({
    // TODO Move to `atoms/BadgeIsOnline`?
    badge: {
      top: '85%',
      right: 6,
      minWidth: 0,
      width: 10,
      height: 10,
      border: `1px solid ${white}`,
      background: colorAccentAloe,
    },
  });

export interface AvatarNodeProps extends WithStyles<typeof styles>, Omit<AvatarProps, 'classes'> {
  fragmentRef: AvatarNode_EmbedNode$key;
  includeIsOnline?: boolean;
  includeTooltip?: boolean;
}

const AvatarNode: React.FC<AvatarNodeProps> = ({
  classes,
  fragmentRef,
  // TODO `AvatarNode.includeIsOnline` default to false
  includeIsOnline = true,
  // TODO `AvatarNode.includeTooltip` default to false
  includeTooltip = true,
  ...rest
}) => {
  const node = useFragment<AvatarNode_EmbedNode$key>(
    graphql`
      fragment AvatarNode_EmbedNode on Node {
        __typename
        ... on Group {
          # Using alias bc of conflicting types String! and String.
          groupName: name
        }
        ... on Patient {
          name
          oid_avatar
          isOnline
        }
        ... on Provider {
          name
          oid_avatar
          isOnline
        }
        ... on User {
          name
          oid_avatar
          isOnline
        }
        ... on ChatActivity {
          patient {
            name
            oid_avatar
            isOnline
          }
        }
      }
    `,
    fragmentRef,
  );

  const { __typename } = node;
  const variant = __typename === 'Group' ? 'group' : 'default';

  const name: string = get(node, 'groupName') || get(node, 'name') || get(node, 'patient.name') || '';
  const isOnline: boolean = get(node, 'isOnline') || get(node, 'patient.isOnline') || false;
  const avatar: string | null = get(node, 'oid_avatar') || get(node, 'patient.oid_avatar') || null;

  const initials = getInitials(name || '');
  const color = stringToColor(initials);

  const src = useMemo(() => {
    if (!avatar) {
      return;
    }
    const token = axiosService.defaults.headers.common['x-access-token'];
    return `${configService.getApiBasePath()}/fileUploads/${avatar}/file?token=${token}`;
  }, [avatar]);

  if (!node) {
    return <Avatar {...rest} />;
  }
  return (
    <Badge
      classes={{ badge: classes.badge }}
      variant="dot"
      invisible={!includeIsOnline || !isOnline}
      onClick={rest.onClick}
    >
      <Avatar variant={variant} color={color} src={src} tooltip={includeTooltip ? name : undefined} {...rest}>
        {src ? undefined : initials}
      </Avatar>
    </Badge>
  );
};

export default withStyles(styles)(AvatarNode);
